import React, { createContext, useContext, useReducer, useCallback, useEffect } from 'react';
import {
  CustomerContextValue,
  CustomerContextState,
  CustomerBrowseItem,
  Engagement,
  CustomerActivationResult,
} from '../types/customer';
import { browseCustomers, activateCustomer } from '../utils/customerApi';

// Action types for the reducer
type CustomerAction =
  | { type: 'SET_LOADING_CUSTOMERS'; payload: boolean }
  | { type: 'SET_ACTIVATING_CUSTOMER'; payload: boolean }
  | { type: 'SET_CUSTOMERS'; payload: CustomerBrowseItem[] }
  | { type: 'SET_SELECTED_CUSTOMER'; payload: CustomerBrowseItem | null }
  | { type: 'SET_SELECTED_ENGAGEMENT'; payload: Engagement | null }
  | { type: 'SET_ERROR'; payload: string | null }
  | { type: 'SET_LAST_FETCHED'; payload: string }
  | { type: 'UPDATE_CUSTOMER_STATUS'; payload: { customerId: string; status: 'ACTIVATED'; documentCount: number } }
  | { type: 'CLEAR_SELECTION' }
  | { type: 'CLEAR_ERROR' };

// Initial state
const initialState: CustomerContextState = {
  selectedCustomer: null,
  selectedEngagement: null,
  availableCustomers: [],
  isLoadingCustomers: false,
  isActivatingCustomer: false,
  error: null,
  lastFetched: null,
};

// Reducer function
const customerReducer = (state: CustomerContextState, action: CustomerAction): CustomerContextState => {
  switch (action.type) {
    case 'SET_LOADING_CUSTOMERS':
      return { ...state, isLoadingCustomers: action.payload };

    case 'SET_ACTIVATING_CUSTOMER':
      return { ...state, isActivatingCustomer: action.payload };

    case 'SET_CUSTOMERS':
      return {
        ...state,
        availableCustomers: action.payload,
        isLoadingCustomers: false,
        error: null,
      };

    case 'SET_SELECTED_CUSTOMER':
      return {
        ...state,
        selectedCustomer: action.payload,
        // Clear engagement when customer changes
        selectedEngagement: null,
      };

    case 'SET_SELECTED_ENGAGEMENT':
      return { ...state, selectedEngagement: action.payload };

    case 'SET_ERROR':
      return {
        ...state,
        error: action.payload,
        isLoadingCustomers: false,
        isActivatingCustomer: false,
      };

    case 'SET_LAST_FETCHED':
      return { ...state, lastFetched: action.payload };

    case 'UPDATE_CUSTOMER_STATUS':
      return {
        ...state,
        availableCustomers: state.availableCustomers.map((customer) =>
          customer.customerId === action.payload.customerId
            ? {
                ...customer,
                status: action.payload.status,
                documentCount: action.payload.documentCount,
                hasDocuments: action.payload.documentCount > 0,
                lastAccessed: new Date().toISOString(),
              }
            : customer,
        ),
        // Update selected customer if it matches
        selectedCustomer:
          state.selectedCustomer?.customerId === action.payload.customerId
            ? {
                ...state.selectedCustomer,
                status: action.payload.status,
                documentCount: action.payload.documentCount,
                hasDocuments: action.payload.documentCount > 0,
                lastAccessed: new Date().toISOString(),
              }
            : state.selectedCustomer,
      };

    case 'CLEAR_SELECTION':
      return {
        ...state,
        selectedCustomer: null,
        selectedEngagement: null,
      };

    case 'CLEAR_ERROR':
      return { ...state, error: null };

    default:
      return state;
  }
};

// Create context
const CustomerContext = createContext<CustomerContextValue | null>(null);

// Provider component
interface CustomerProviderProps {
  children: React.ReactNode;
}

export const CustomerProvider: React.FC<CustomerProviderProps> = ({ children }) => {
  const [state, dispatch] = useReducer(customerReducer, initialState);

  // Fetch customers from API
  const fetchCustomers = useCallback(async () => {
    try {
      dispatch({ type: 'SET_LOADING_CUSTOMERS', payload: true });
      dispatch({ type: 'CLEAR_ERROR' });

      const customers = await browseCustomers();

      dispatch({ type: 'SET_CUSTOMERS', payload: customers });
      dispatch({ type: 'SET_LAST_FETCHED', payload: new Date().toISOString() });
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to fetch customers';
      dispatch({ type: 'SET_ERROR', payload: errorMessage });
    }
  }, []);

  // Refresh customers (force refetch)
  const refreshCustomers = useCallback(async () => {
    await fetchCustomers();
  }, [fetchCustomers]);

  // Select a customer
  const selectCustomer = useCallback((customer: CustomerBrowseItem) => {
    dispatch({ type: 'SET_SELECTED_CUSTOMER', payload: customer });

    // If customer is activated and has a default engagement, set it
    if (customer.status === 'ACTIVATED') {
      // Create a default engagement object for the selected customer
      const defaultEngagement: Engagement = {
        engagementId: `${customer.customerId}-default`,
        customerId: customer.customerId,
        name: `${customer.name} Default Engagement`,
        isDefault: true,
        type: 'COMBINED',
        createdAt: new Date().toISOString(),
        createdBy: 'SYSTEM',
        status: 'ACTIVE',
      };
      dispatch({ type: 'SET_SELECTED_ENGAGEMENT', payload: defaultEngagement });
    }
  }, []);

  // Activate a customer
  const handleActivateCustomer = useCallback(
    async (customerId: string): Promise<CustomerActivationResult | null> => {
      try {
        dispatch({ type: 'SET_ACTIVATING_CUSTOMER', payload: true });
        dispatch({ type: 'CLEAR_ERROR' });

        const result = await activateCustomer(customerId);

        // Update the customer status in our state
        dispatch({
          type: 'UPDATE_CUSTOMER_STATUS',
          payload: {
            customerId,
            status: 'ACTIVATED',
            documentCount: result.repository.documentCount,
          },
        });

        // If this is the selected customer, update the engagement
        if (state.selectedCustomer?.customerId === customerId) {
          dispatch({ type: 'SET_SELECTED_ENGAGEMENT', payload: result.engagement });
        }

        dispatch({ type: 'SET_ACTIVATING_CUSTOMER', payload: false });
        return result;
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : 'Failed to activate customer';
        dispatch({ type: 'SET_ERROR', payload: errorMessage });
        return null;
      }
    },
    [state.selectedCustomer],
  );

  // Clear selection
  const clearSelection = useCallback(() => {
    dispatch({ type: 'CLEAR_SELECTION' });
  }, []);

  // Clear error
  const clearError = useCallback(() => {
    dispatch({ type: 'CLEAR_ERROR' });
  }, []);

  // Auto-fetch customers on mount
  useEffect(() => {
    fetchCustomers();
  }, [fetchCustomers]);

  // Context value
  const contextValue: CustomerContextValue = {
    // State
    ...state,

    // Actions
    fetchCustomers,
    selectCustomer,
    activateCustomer: handleActivateCustomer,
    clearSelection,
    clearError,
    refreshCustomers,
  };

  return <CustomerContext.Provider value={contextValue}>{children}</CustomerContext.Provider>;
};

// Custom hook to use the customer context
export const useCustomerContext = (): CustomerContextValue => {
  const context = useContext(CustomerContext);

  if (!context) {
    throw new Error('useCustomerContext must be used within a CustomerProvider');
  }

  return context;
};
